<template>
  <UserCommonLayout :is-open-filter="isOpenFilterFlag" :having-filter="true">
    <template #sidebar>
      <div v-if="isLoading" class="sideWrap">
        <LazySkillAdvanceCommonFilter
          :is-my-data="true"
          @filter-toggle-on-class="filterToggleOnClass"/>
        <div class="rightContWrap">
          <main id="dashboardContent">
            <LazySkillAdvanceList
              :is-open-filter="isOpenFilterFlag"
              :advance-page-data="myAdvances"
              :advance-type="SkillAdvanceType.LEARN"
              :is-my-data="true"
              @emit-track-list="getMyAdvanceList"
              @filter-toggle-on-class="filterToggleOnClass"
            />
          </main>
          <LazyUserCommonFooter />
        </div>
      </div>
    </template>
  </UserCommonLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useRoute, watch } from "#imports";
import { LazySkillAdvanceCommonFilter, LazySkillAdvanceList, UserCommonLayout } from "#components";
import { useTrackService } from "~/composables/service/useTrackService";
import { useNuxtApp } from "#app";
import { useLoadingStore } from "~/store/loading/loadingStore";
import { PageModel } from "~~/package/mapper/common/model/PageModel";
import { useI18n } from "vue-i18n";
import type { UnwrapRef } from "vue";
import { SkillAdvanceType } from "~~/package/mapper/track/model/SkillAdvanceType";
import { SkillAdvanceMyTrackModel } from "~~/package/mapper/track/model/SkillAdvanceMyTrackModel";
import { TrackStatusType } from "~~/package/mapper/track/model/TrackStatusType";

const { $i18n } = useNuxtApp();
const route = useRoute();
const loadingStore = useLoadingStore();

const isLoading = ref(true);
const myAdvances = ref(new PageModel<SkillAdvanceMyTrackModel>());
const page = ref(1);
const beforeType = ref<UnwrapRef<null | string>>(null);

const isOpenFilterFlag = ref(false);

onMounted(() => {
  isOpenFilterFlag.value = window.innerWidth > 1080;
});
const field = ref<string | undefined>(undefined);
const status = ref<TrackStatusType | undefined>(undefined);

const filters = computed(() => {
  if (!route.query.filter) return [];

  return Array.isArray(route.query.filter)
    ? route.query.filter
    : [route.query.filter];
});

const transFilterString = computed(() => {
  const filterMap = new Map<string, string>();
  let hasFieldKey = false;
  let hasStatusKey = false;

  filters.value.forEach((filter) => {
    const [key, value] = (filter as string).split(":");
    if (key === "field") {
      field.value = value;
      hasFieldKey = true;
    } else if (key === "status"){
      status.value = value as TrackStatusType;
      hasStatusKey = true;
    } else {
      const existingValue = filterMap.get(key);
      filterMap.set(key, existingValue ? `${existingValue}|${value}` : value);
    }
  });

  if(!hasFieldKey) field.value = undefined;
  if(!hasStatusKey) status.value = undefined;

  return Array.from(filterMap, ([key, value]) => `${key}:${value}`);
});

const fetchMyAdvanceList = (pageNum: number, filter: string[]) => {
  return useTrackService().getMyAdvanceList(
    pageNum,
    9,
    $i18n.locale.value,
    filter,
    SkillAdvanceType.LEARN,
    field.value,
    status.value
  );
};

const initData = (filter: string[]) => {
  fetchMyAdvanceList(1, filter).then((resolve) => {
    myAdvances.value = resolve;
    page.value = 2;
    loadingStore.closeLoading();
    isLoading.value = !loadingStore.show;
  });
};

const getMyAdvanceList = () => {
  fetchMyAdvanceList(page.value, transFilterString.value).then((trackPageData) => {
    if (page.value !== 1) {
      trackPageData.data = [...myAdvances.value.data, ...trackPageData.data];
    }
    myAdvances.value = trackPageData;
    page.value += 1;
  });
};

const filterToggleOnClass = (flag: boolean) => {
  isOpenFilterFlag.value = flag;
}

watch(
  () => route.query,
  () => {
    if (beforeType.value !== route.query.type) {
      loadingStore.openLoading("light");
      isLoading.value = false;
    }
    page.value = 1;
    initData(transFilterString.value);
    beforeType.value = route.query.type as string;
  },
  { immediate: true }
);
</script>
